<template>
  <div class="message-composer">
    <SocialMessageComposer
      v-if="isSocialChannel"
      ref="socialComposerRef"
      :conversation="conversation"
      :reply-to="replyTo"
      :channel-status="channelStatus"
      :is-sending="isSending"
      :show-ai-buttons="showAiButtons"
      :ai-buttons-disabled="aiButtonsDisabled"
      :is-generating-a-i="isGeneratingAI"
      :contact-id="contactId"
      :icp-id="icpId"
      :icps="icps"
      :generating-action="generatingAction"
      :channel-name="channelName"
      @send-message="handleSocialSend"
      @cancel-reply="$emit('cancel-reply')"
      @ai-reply="(payload: any) => $emit('ai-reply', payload)"
      @ai-followup="(payload: any) => $emit('ai-followup', payload)"
      @ai-compose="(payload: any) => $emit('ai-compose', payload)"
    />
    <template v-else>
      <div
        v-if="conversation && conversation.channel === 'email'"
        class="message-composer__reply-header"
      >
        <span class="message-composer__reply-text">Replying to {{ conversation.contactName }}</span>
        <div class="message-composer__reply-actions">
          <Icon icon="mdi:send" />
          <Icon icon="mdi:delete-outline" />
        </div>
      </div>
      <div
        v-if="conversation && conversation.channel === 'email'"
        class="message-composer__subject"
      >
        <input
          v-model="subject"
          type="text"
          placeholder="Subject"
          class="message-composer__subject-input"
        />
        <button class="message-composer__hide-btn" @click="showSubject = !showSubject">
          {{ showSubject ? "Hide" : "Show" }}
        </button>
      </div>
      <div
        v-if="conversation && conversation.channel === 'email' && showCcBcc"
        class="message-composer__recipients"
      >
        <input
          v-model="cc"
          type="text"
          placeholder="CC (comma-separated emails)"
          class="message-composer__recipient-input"
        />
        <input
          v-model="bcc"
          type="text"
          placeholder="BCC (comma-separated emails)"
          class="message-composer__recipient-input"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { Conversation, Message } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { computed, ref } from "vue";
import SocialMessageComposer from "./SocialMessageComposer.vue";

const socialComposerRef = ref<InstanceType<typeof SocialMessageComposer> | null>(null);

defineExpose({
  setContent: (content: string) => {
    if (socialComposerRef.value) {
      (socialComposerRef.value as any).setContent?.(content);
    }
  },
  socialComposer: socialComposerRef,
});

interface Props {
  conversation: Conversation | null;
  replyTo?: Message | null;
  channelStatus?: string;
  isSending?: boolean;
  showAiButtons?: boolean;
  aiButtonsDisabled?: boolean;
  isGeneratingAI?: boolean;
  generatingAction?: string | null;
  contactId?: string;
  icpId?: string;
  icps?: Array<{ id: string; name: string; isDefault?: boolean }>;
  channelName?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isSending: false,
  showAiButtons: false,
  aiButtonsDisabled: false,
  isGeneratingAI: false,
});

const emit = defineEmits<{
  "send-message": [
    data: {
      text?: string;
      subject?: string;
      cc?: string[];
      bcc?: string[];
      quote_id?: string;
      attachments?: File[];
      voice_message?: File;
      video_message?: File;
    },
  ];
  "cancel-reply": [];
  "ai-reply": [payload: { icpId?: string }];
  "ai-followup": [payload: { icpId?: string }];
  "ai-compose": [payload: { icpId?: string; customInstructions?: string }];
}>();

const subject = ref(props.replyTo?.subject ? `Re: ${props.replyTo.subject}` : "");
const cc = ref("");
const bcc = ref("");
const showSubject = ref(true);
const showCcBcc = ref(false);

// Check if this is a social channel (WhatsApp or LinkedIn)
const isSocialChannel = computed(() => {
  const channel = props.conversation?.channel;
  return channel === "whatsapp" || channel === "linkedin";
});

const handleSocialSend = (data: {
  text?: string;
  quote_id?: string;
  attachments?: File[];
  voice_message?: File;
  video_message?: File;
}) => {
  emit("send-message", {
    text: data.text,
    quote_id: data.quote_id,
    attachments: data.attachments,
    voice_message: data.voice_message,
    video_message: data.video_message,
  });
};
</script>

<style scoped>
.message-composer {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
}

.message-composer__reply-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.message-composer__reply-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.message-composer__reply-actions {
  display: flex;
  gap: 0.5rem;
  color: #6b7280;
}

.message-composer__subject {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.message-composer__subject-input {
  flex: 1;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
}

.message-composer__hide-btn {
  padding: 0.5rem 0.75rem;
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
}

.message-composer__recipients {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.message-composer__recipient-input {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
}

.message-composer__open-modal-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;
  color: #374151;
}

.message-composer__open-modal-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.message-composer__open-modal-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.message-composer__open-modal-btn .iconify {
  font-size: 1.25rem;
}
</style>
